package diana.soleil.design.iraniancanadienbazzar.navigation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BrandMark
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import iraniancanadienbazzar.shared.generated.resources.Res
import iraniancanadienbazzar.shared.generated.resources.gate_debug_dismiss
import iraniancanadienbazzar.shared.generated.resources.gate_maintenance_message_default
import iraniancanadienbazzar.shared.generated.resources.gate_maintenance_title
import iraniancanadienbazzar.shared.generated.resources.gate_retry
import iraniancanadienbazzar.shared.generated.resources.gate_update_button
import iraniancanadienbazzar.shared.generated.resources.gate_update_message_default
import iraniancanadienbazzar.shared.generated.resources.gate_update_title
import org.jetbrains.compose.resources.stringResource

/**
 * Full-screen blocking gates rendered ABOVE navigation (App.kt), driven by Remote Config via
 * `AppGatesConfig` — see docs/DEVELOPER_MENU.md for the QA overrides.
 *
 * Shared scaffold: brand mark + status icon + title + body + action, centered, reading-width capped.
 * [debugDismiss] renders a low-emphasis escape hatch ONLY when a Developer Menu override forced the
 * gate (debug builds), so QA can back out without relaunching.
 */
@Composable
private fun GateScaffold(
    iconName: String,
    title: String,
    message: String,
    actionLabel: String,
    onAction: () -> Unit,
    debugDismiss: (() -> Unit)?,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(AppTheme.colors.background)
            .padding(AppTheme.spacing.xl),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        BrandMark(
            contentDescription = null,
            modifier = Modifier.size(AppTheme.sizes.brandMark),
        )
        AppIcon(
            name = iconName,
            tint = AppTheme.colors.primary,
            size = AppTheme.sizes.iconLg,
            modifier = Modifier.padding(top = AppTheme.spacing.xl),
        )
        Text(
            text = title,
            style = AppTheme.typography.headlineMd,
            color = AppTheme.colors.onSurface,
            textAlign = TextAlign.Center,
            modifier = Modifier.padding(top = AppTheme.spacing.lg),
        )
        Text(
            text = message,
            style = AppTheme.typography.bodyMd,
            color = AppTheme.colors.onSurfaceVariant,
            textAlign = TextAlign.Center,
            modifier = Modifier
                .widthIn(max = AppTheme.sizes.contentMaxWidthCompact)
                .padding(top = AppTheme.spacing.sm),
        )
        AppButton(
            text = actionLabel,
            onClick = onAction,
            modifier = Modifier.padding(top = AppTheme.spacing.xl),
        )
        if (debugDismiss != null) {
            AppButton(
                text = stringResource(Res.string.gate_debug_dismiss),
                onClick = debugDismiss,
                variant = AppButtonVariant.Text,
                modifier = Modifier.padding(top = AppTheme.spacing.md),
            )
        }
    }
}

/** Backend-down/maintenance gate. [message] empty → bundled default. [onRetry] re-fetches config. */
@Composable
fun MaintenanceGateScreen(
    message: String,
    onRetry: () -> Unit,
    debugDismiss: (() -> Unit)? = null,
) {
    GateScaffold(
        iconName = "engineering",
        title = stringResource(Res.string.gate_maintenance_title),
        message = message.ifBlank { stringResource(Res.string.gate_maintenance_message_default) },
        actionLabel = stringResource(Res.string.gate_retry),
        onAction = onRetry,
        debugDismiss = debugDismiss,
    )
}

/** Below-minimum-version gate. [onUpdate] opens the store/web listing. No bypass in release. */
@Composable
fun ForceUpdateGateScreen(
    onUpdate: () -> Unit,
    debugDismiss: (() -> Unit)? = null,
) {
    GateScaffold(
        iconName = "system_update",
        title = stringResource(Res.string.gate_update_title),
        message = stringResource(Res.string.gate_update_message_default),
        actionLabel = stringResource(Res.string.gate_update_button),
        onAction = onUpdate,
        debugDismiss = debugDismiss,
    )
}
